/*
  AssessmentRecovery — dedicated page listing every incomplete assessment.

  Complements the in-dialog recovery banner. Two advantages over the
  banner:
    1. Always accessible — no need to open the recording dialog.
    2. Shows sessions whose audio has been lost (e.g. the production
       container was recycled and wiped `/app/data/soap_audio/`),
       giving the operator visibility into "orphan" DB rows they can
       tidy up.

  Sessions with audio on disk can be Retried (kicks the background
  finalize job and polls until the note lands). Sessions whose audio
  is gone can only be Discarded.
*/
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useRecording } from "@/lib/recording";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertTriangle, CheckCircle2, HardDriveDownload, Loader2, RotateCw,
  Trash2, ArrowRight, RefreshCw, Info, XCircle, Download, Archive, Clock,
  Play, SkipForward, FileText, Link2,
} from "lucide-react";
import { BackLink } from "@/components/BackLink";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function bytesHuman(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function AssessmentRecovery() {
  const rec = useRecording();
  const navigate = useNavigate();
  const [tab, setTab] = useState("incomplete");   // incomplete | retained
  const [sessions, setSessions] = useState([]);
  const [retained, setRetained] = useState([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [loadingRetained, setLoadingRetained] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [playbackSession, setPlaybackSession] = useState(null); // {id, name, ...} of session under review

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/soap-notes/sessions/incomplete");
      setSessions(r.data.sessions || []);
    } catch (e) {
      toast.error("Failed to load", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshRetained = useCallback(async () => {
    setLoadingRetained(true);
    try {
      const r = await api.get("/soap-notes/sessions/retained");
      setRetained(r.data.sessions || []);
      setRetentionDays(r.data.retention_days || 30);
    } catch (e) {
      toast.error("Failed to load retained recordings", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoadingRetained(false);
    }
  }, []);

  useEffect(() => { refresh(); refreshRetained(); }, [refresh, refreshRetained]);

  const stats = useMemo(() => {
    const s = { retriable: 0, lost: 0, running: 0 };
    for (const x of sessions) {
      if (x.running) s.running += 1;
      if (x.audio_available) s.retriable += 1;
      else s.lost += 1;
    }
    return s;
  }, [sessions]);

  async function retry(sess) {
    setBusyId(sess.id);
    try {
      const res = await rec.retryFinalize(sess.id, {
        extra_context: sess.therapist_notes || "",
        include_sbn: !!sess.sbn_client_id,
        template_id: sess.template_id || null,
      });
      toast.success("SOAP note generated", {
        description: `Recovered ${sess.client_name || sess.client_email}. Opening the note now.`,
      });
      await refresh();
      // Deep-link into the client's Notes tab so the operator can review & sign off.
      if (res?.session?.client_email) {
        navigate(`/clients/${encodeURIComponent(res.session.client_email)}?tab=notes`);
      }
    } catch (e) {
      toast.error("Retry failed", {
        description: e?.message || "Please try again in a minute.",
      });
    } finally {
      setBusyId(null);
    }
  }

  async function discard(sess) {
    const hasAudio = sess.audio_available;
    const label = sess.client_name || sess.client_email;
    const msg = hasAudio
      ? `Permanently delete the recording for ${label}?\n\nThis cannot be undone — the audio segments will be removed.`
      : `Remove the orphaned session for ${label}?\n\nNo audio is available so nothing more can be done with it.`;
    if (!window.confirm(msg)) return;
    setBusyId(sess.id);
    try {
      await api.delete(`/soap-notes/sessions/${sess.id}?force=true`);
      toast.success(hasAudio ? "Recording discarded" : "Orphan removed");
      await refresh();
      await refreshRetained();
    } catch (e) {
      toast.error("Delete failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusyId(null);
    }
  }

  async function downloadAudio(sess) {
    try {
      const r = await api.get(`/soap-notes/sessions/${sess.id}/audio.zip`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/zip" });
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const slug = (sess.client_name || sess.client_email || "session").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9\-_]+/g, "");
      a.download = `recording-${slug || "session"}-${sess.id.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success("Recording downloaded");
    } catch (e) {
      toast.error("Download failed", {
        description: e?.response?.status === 410
          ? "Audio has already been purged."
          : (e?.response?.data?.detail || e.message),
      });
    }
  }

  async function regenerate(sess) {
    if (!window.confirm(`Regenerate the SOAP note for ${sess.client_name || sess.client_email}?\n\nA fresh transcription + LLM pass will run using the stored audio. The previous note remains in place until the new one is ready.`)) return;
    setBusyId(sess.id);
    try {
      await api.post(`/soap-notes/sessions/${sess.id}/regenerate`, {
        extra_context: sess.therapist_notes || "",
        include_sbn: !!sess.sbn_client_id,
        template_id: sess.template_id || null,
      });
      // Poll to completion using the recording provider helper so we get
      // the same UX as the retry flow.
      const res = await rec.pollUntilDone(sess.id);
      toast.success("SOAP note regenerated", {
        description: `New note ready for ${sess.client_name || sess.client_email}.`,
      });
      await refreshRetained();
      if (res?.session?.client_email) {
        navigate(`/clients/${encodeURIComponent(res.session.client_email)}?tab=notes`);
      }
    } catch (e) {
      toast.error("Regenerate failed", {
        description: e?.message || "Please try again in a minute.",
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-[1200px] px-6 sm:px-8 md:px-10 py-8">
      <BackLink to="/assessments" label="Back to Assessments" testId="recovery-back" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
            <HardDriveDownload className="h-3.5 w-3.5" /> Assessment recovery
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Recordings vault
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
            Every voice recording is retained for <b>{retentionDays} days</b> after the session finishes,
            so you can always download the raw audio or regenerate the SOAP note. Interrupted sessions
            with audio still on disk can be retried; orphans whose audio was lost can be discarded.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => { refresh(); refreshRetained(); }}
          disabled={loading || loadingRetained}
          className="border-zinc-300"
          data-testid="recovery-refresh-btn"
        >
          <RefreshCw className={cn("h-4 w-4 mr-1.5", (loading || loadingRetained) && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-zinc-200 flex gap-4">
        <TabButton
          active={tab === "incomplete"}
          onClick={() => setTab("incomplete")}
          testid="tab-incomplete"
        >
          <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
          Interrupted <Pill>{sessions.length}</Pill>
        </TabButton>
        <TabButton
          active={tab === "retained"}
          onClick={() => setTab("retained")}
          testid="tab-retained"
        >
          <Archive className="h-3.5 w-3.5 mr-1.5" />
          Retained recordings <Pill>{retained.length}</Pill>
        </TabButton>
      </div>

      {tab === "incomplete" && (
        <>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard label="Ready to retry" value={stats.retriable} tone="ok" />
            <StatCard label="Audio lost" value={stats.lost} tone={stats.lost > 0 ? "warn" : "neutral"} />
            <StatCard label="Currently running" value={stats.running} tone={stats.running > 0 ? "info" : "neutral"} />
          </div>

          <Card className="mt-6 rounded-lg border border-zinc-200 shadow-none">
            {loading ? (
              <div className="p-10 text-center text-sm text-zinc-500">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading incomplete sessions…
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-10 text-center">
                <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500" />
                <p className="mt-3 text-sm text-zinc-700 font-medium">All clear</p>
                <p className="mt-1 text-xs text-zinc-500">
                  Every assessment has either produced a SOAP note or been discarded.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100" data-testid="recovery-list">
                {sessions.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    busy={busyId === s.id || s.running}
                    onRetry={() => retry(s)}
                    onDiscard={() => discard(s)}
                  />
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      {tab === "retained" && (
        <Card className="mt-6 rounded-lg border border-zinc-200 shadow-none">
          {loadingRetained ? (
            <div className="p-10 text-center text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading retained recordings…
            </div>
          ) : retained.length === 0 ? (
            <div className="p-10 text-center">
              <Archive className="h-8 w-8 mx-auto text-zinc-400" />
              <p className="mt-3 text-sm text-zinc-700 font-medium">Nothing in the vault yet</p>
              <p className="mt-1 text-xs text-zinc-500">
                Recordings appear here after a session ends and remain for {retentionDays} days.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="retained-list">
              {retained.map((s) => (
                <RetainedRow
                  key={s.id}
                  session={s}
                  busy={busyId === s.id || s.running}
                  onDownload={() => downloadAudio(s)}
                  onPlay={() => setPlaybackSession(s)}
                  onRegenerate={() => regenerate(s)}
                  onDiscard={() => discard(s)}
                />
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Playback dialog — plays stored audio + shows the existing note side-by-side */}
      {playbackSession && (
        <PlaybackDialog
          session={playbackSession}
          onClose={() => setPlaybackSession(null)}
          onRegenerate={() => { const s = playbackSession; setPlaybackSession(null); regenerate(s); }}
        />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "-mb-px inline-flex items-center px-1 pb-3 text-sm border-b-2 transition-colors",
        active ? "border-zinc-950 text-zinc-950 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-800",
      )}
    >
      {children}
    </button>
  );
}

function Pill({ children }) {
  return (
    <span className="ml-1.5 text-2xs font-mono-num tabular-nums px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200">
      {children}
    </span>
  );
}

function RetainedRow({ session, busy, onDownload, onPlay, onRegenerate, onDiscard }) {
  const s = session;
  const audioOk = s.audio_available;
  const days = s.retention?.days_remaining;
  const navigate = useNavigate();
  const isFinal = s.status === "done" && s.note_id;
  return (
    <li className="p-4 flex items-center gap-4 flex-wrap" data-testid={`retained-row-${s.id}`}>
      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-zinc-950 text-sm">
            {s.client_name || s.client_email}
          </span>
          {s.appointment_type && (
            <span className="text-2xs text-zinc-500">· {s.appointment_type}</span>
          )}
          {isFinal ? (
            <span className="text-2xs px-1.5 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800">
              note ready
            </span>
          ) : (
            <StatusPill status={s.status} audioOk={audioOk} running={s.running} />
          )}
          {typeof days === "number" && (
            <span
              className={cn(
                "text-2xs px-1.5 py-0.5 rounded-full border inline-flex items-center gap-1",
                days <= 3 ? "bg-red-50 text-red-800 border-red-200"
                : days <= 7 ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-zinc-50 text-zinc-600 border-zinc-200",
              )}
            >
              <Clock className="h-2.5 w-2.5" />
              {days === 0 ? "expires today" : `${days}d retention left`}
            </span>
          )}
        </div>
        <div className="text-2xs text-zinc-500 mt-1 font-mono-num flex flex-wrap gap-x-3 gap-y-0.5">
          <span>Started {fmtDate(s.started_at)}</span>
          {audioOk && (
            <span>
              {s.audio.segments} segment{s.audio.segments === 1 ? "" : "s"} · {bytesHuman(s.audio.bytes)}
            </span>
          )}
          {!audioOk && (
            <span className="text-zinc-500 flex items-center gap-1">
              <XCircle className="h-3 w-3" /> Audio no longer on disk
            </span>
          )}
        </div>
        {s.client_email && (
          <button
            onClick={() => navigate(`/clients/${encodeURIComponent(s.client_email)}${isFinal ? "?tab=notes" : ""}`)}
            className="text-2xs text-zinc-500 hover:text-zinc-900 mt-1 inline-flex items-center gap-1"
            data-testid={`retained-client-link-${s.id}`}
          >
            Open client <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {audioOk && (
          <>
            <Button
              onClick={onPlay}
              disabled={busy}
              variant="outline"
              className="border-zinc-300 h-9"
              data-testid={`retained-play-${s.id}`}
            >
              <Play className="h-3.5 w-3.5 mr-1.5" /> Play
            </Button>
            <Button
              onClick={onDownload}
              disabled={busy}
              variant="outline"
              className="border-zinc-300 h-9"
              data-testid={`retained-download-${s.id}`}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Download
            </Button>
            <Button
              onClick={onRegenerate}
              disabled={busy}
              className="bg-amber-700 hover:bg-amber-800 text-white h-9"
              data-testid={`retained-regenerate-${s.id}`}
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCw className="h-3.5 w-3.5 mr-1.5" />
              )}
              Regenerate note
            </Button>
          </>
        )}
        <Button
          variant="outline"
          onClick={onDiscard}
          disabled={busy}
          className="border-red-300 text-red-700 hover:bg-red-50 h-9"
          data-testid={`retained-discard-${s.id}`}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Discard
        </Button>
      </div>
    </li>
  );
}

function SessionRow({ session, busy, onRetry, onDiscard }) {
  const s = session;
  const audioOk = s.audio_available;
  const navigate = useNavigate();
  const [linkOpen, setLinkOpen] = useState(false);
  return (
    <li className="p-4 flex items-center gap-4 flex-wrap" data-testid={`recovery-row-${s.id}`}>
      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-zinc-950 text-sm">
            {s.client_name || s.client_email}
          </span>
          {s.appointment_type && (
            <span className="text-2xs text-zinc-500">· {s.appointment_type}</span>
          )}
          <StatusPill status={s.status} audioOk={audioOk} running={s.running} />
          {s.initiating_assessment_id ? (
            <span
              className="text-2xs uppercase tracking-wider text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
              title="Linked to an assessment — the transcript will flow into it once finalised"
              data-testid={`recovery-linked-${s.id}`}
            >
              <Link2 className="h-3 w-3" /> Linked to assessment
            </span>
          ) : null}
        </div>
        <div className="text-2xs text-zinc-500 mt-1 font-mono-num flex flex-wrap gap-x-3 gap-y-0.5">
          <span>Started {fmtDate(s.started_at)}</span>
          {audioOk ? (
            <span>
              {s.audio.segments} segment{s.audio.segments === 1 ? "" : "s"} · {bytesHuman(s.audio.bytes)}
            </span>
          ) : (
            <span className="text-red-700 flex items-center gap-1">
              <XCircle className="h-3 w-3" /> Audio no longer on disk
            </span>
          )}
          {s.error && (
            <span className="text-red-700">Error: {s.error}</span>
          )}
        </div>
        {s.client_email && (
          <button
            onClick={() => navigate(`/clients/${encodeURIComponent(s.client_email)}`)}
            className="text-2xs text-zinc-500 hover:text-zinc-900 mt-1 inline-flex items-center gap-1"
            data-testid={`recovery-client-link-${s.id}`}
          >
            Open client <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {audioOk ? (
          <Button
            onClick={onRetry}
            disabled={busy}
            className="bg-amber-700 hover:bg-amber-800 text-white h-9"
            data-testid={`recovery-retry-${s.id}`}
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RotateCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            {s.running ? "Running…" : busy ? "Working…" : "Retry"}
          </Button>
        ) : (
          <span
            className="text-2xs text-zinc-500 italic px-3 py-2 rounded border border-zinc-200 bg-zinc-50"
            title="No audio to retry — the original recording is no longer available on disk"
            data-testid={`recovery-nofix-${s.id}`}
          >
            Cannot recover
          </span>
        )}
        <Button
          variant="outline"
          onClick={() => setLinkOpen(true)}
          disabled={busy}
          className="h-9"
          data-testid={`recovery-link-assessment-${s.id}`}
          title="Attach this recording to one of the client's open assessments so the transcript populates it"
        >
          <Link2 className="h-3.5 w-3.5 mr-1.5" />
          {s.initiating_assessment_id ? "Change link" : "Link to assessment"}
        </Button>
        <Button
          variant="outline"
          onClick={onDiscard}
          disabled={busy}
          className="border-red-300 text-red-700 hover:bg-red-50 h-9"
          data-testid={`recovery-discard-${s.id}`}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Discard
        </Button>
      </div>
      <LinkToAssessmentDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        session={s}
      />
    </li>
  );
}


function LinkToAssessmentDialog({ open, onClose, session }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [selectedId, setSelectedId] = useState(session?.initiating_assessment_id || "");

  useEffect(() => {
    if (!open || !session?.client_email) return;
    setLoading(true);
    // The list endpoint filters by ``client_id`` (formatted as
    // ``email:{email}``) and returns a bare array. We ask for
    // draft-status assessments only so a finalised note doesn't
    // pollute the picker.
    api.get("/assessments", {
      params: { client_id: `email:${session.client_email}` },
    })
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : (r.data?.assessments || []);
        // Filter out already-finalised assessments — you can't attach
        // a transcript to a locked note.
        setAssessments(rows.filter((a) => a.status !== "finalised"));
      })
      .catch(() => setAssessments([]))
      .finally(() => setLoading(false));
  }, [open, session?.client_email]);

  async function save() {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.post(`/soap-notes/sessions/${session.id}/link-assessment`, {
        assessment_id: selectedId,
      });
      // Auto-extract — kicks the transcript suggestion pipeline
      // immediately if the SOAP session already has a transcript.
      // Best-effort: recovery-vault sessions are usually mid-flight
      // and won't have one yet, so a 400 "no transcript" is
      // expected here; we swallow it silently. Once the recording
      // finishes finalising, the backend finalise task auto-runs
      // the extractor for the same reason.
      try {
        await api.post(`/assessments/${selectedId}/suggest-from-transcript`);
      } catch (extractErr) {
        const status = extractErr?.response?.status;
        const msg = extractErr?.response?.data?.detail || "";
        if (status && status !== 400 && !/transcript/i.test(msg)) {
          toast.warning("Recording linked, but auto-suggest failed", {
            description: msg,
          });
        }
      }
      toast.success("Recording linked to assessment", {
        description: "Retry the recording next — the transcript will land on the assessment automatically.",
      });
      onClose(true);
    } catch (e) {
      toast.error("Link failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(false); }}>
      <DialogContent className="max-w-lg" data-testid="link-assessment-dialog">
        <DialogHeader>
          <DialogTitle>Link recording to assessment</DialogTitle>
          <DialogDescription>
            Choose one of {session?.client_name || session?.client_email}&apos;s open assessments.
            The transcript from this recording will flow into it after Retry.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-6 text-center text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 mx-auto mb-2 animate-spin" /> Loading assessments…
          </div>
        ) : assessments.length === 0 ? (
          <div className="py-6 text-center text-sm text-zinc-500" data-testid="link-assessment-empty">
            No assessments found for this client. Open the client&apos;s Assessments tab to create one first.
          </div>
        ) : (
          <ul className="max-h-72 overflow-y-auto divide-y divide-zinc-100 border border-zinc-200 rounded-md">
            {assessments.map((a) => (
              <li key={a.id}>
                <label className="flex items-center gap-2 p-2 cursor-pointer hover:bg-zinc-50">
                  <input
                    type="radio"
                    name="pick-assess"
                    checked={selectedId === a.id}
                    onChange={() => setSelectedId(a.id)}
                    data-testid={`link-assessment-radio-${a.id}`}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-950 truncate">
                      {(a.modules || []).map((m) => m.key).join(" + ") || "Assessment"}
                    </div>
                    <div className="text-2xs text-zinc-500">
                      {a.status} · started {new Date(a.started_at || a.created_at).toLocaleString()}
                    </div>
                  </div>
                </label>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onClose(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={!selectedId || saving}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="link-assessment-save"
          >
            {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Linking…</> : "Link"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ status, audioOk, running }) {
  const tone =
    running          ? "bg-blue-50 text-blue-800 border-blue-200" :
    status === "failed"   ? "bg-red-50 text-red-700 border-red-200" :
    status === "finalizing" ? "bg-amber-50 text-amber-800 border-amber-200" :
    status === "recording"  ? "bg-red-50 text-red-700 border-red-200" :
    "bg-zinc-100 text-zinc-600 border-zinc-200";
  const label = running ? "running" : status || "unknown";
  return (
    <span className={cn("text-2xs px-1.5 py-0.5 rounded-full border capitalize", tone)}>
      {label}
      {!audioOk && !running && <> · no audio</>}
    </span>
  );
}

function StatCard({ label, value, tone = "neutral" }) {
  const toneClasses = {
    ok:      "border-emerald-200 bg-emerald-50",
    warn:    "border-amber-200 bg-amber-50",
    info:    "border-blue-200 bg-blue-50",
    neutral: "border-zinc-200 bg-white",
  }[tone];
  const numTone = {
    ok: "text-emerald-800", warn: "text-amber-800", info: "text-blue-800", neutral: "text-zinc-950",
  }[tone];
  return (
    <Card className={cn("p-3 rounded-md border shadow-none", toneClasses)}>
      <div className="text-2xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={cn("mt-1 text-3xl font-mono-num tabular-nums", numTone)}>{value}</div>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// PlaybackDialog — audio player + SOAP note side-by-side.
//
// Segments are self-contained webm files (~8 min each). We fetch them one
// at a time as blobs (so the browser sends cookies automatically), then
// swap the `<audio>` src to the next segment when the current one ends
// — the result plays like one continuous recording.
//
// If the session has a finalized note we pull it and render subjective /
// objective / assessment / plan alongside so the operator can read the
// existing note while listening, and decide whether it's worth regenerating.
// ---------------------------------------------------------------------------
function PlaybackDialog({ session, onClose, onRegenerate }) {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(null);   // blob: URL
  const [loadingSegment, setLoadingSegment] = useState(false);
  const [note, setNote] = useState(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // Bootstrap — fetch segments list + note payload in parallel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/soap-notes/sessions/${session.id}/segments`);
        if (!cancelled) setSegments(r.data.segments || []);
      } catch (e) {
        if (!cancelled) toast.error("Could not load segments", {
          description: e?.response?.data?.detail || e.message,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    if (session.note_id) {
      setNoteLoading(true);
      api.get(`/soap-notes/notes/${session.note_id}`)
        .then((r) => { if (!cancelled) setNote(r.data); })
        .catch(() => { /* note optional — silent fail */ })
        .finally(() => { if (!cancelled) setNoteLoading(false); });
    }
    return () => { cancelled = true; };
  }, [session.id, session.note_id]);

  // Fetch a segment as a blob URL so the browser can play it inline.
  const loadSegment = useCallback(async (idx) => {
    if (idx < 0 || idx >= segments.length) return;
    setLoadingSegment(true);
    setCurrentIndex(idx);
    try {
      const r = await api.get(
        `/soap-notes/sessions/${session.id}/segments/${segments[idx].index}.webm`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "audio/webm" });
      const url = URL.createObjectURL(blob);
      setCurrentSrc((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      toast.error("Could not load segment", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoadingSegment(false);
    }
  }, [segments, session.id]);

  // Autoload segment 0 the moment we have the list.
  useEffect(() => {
    if (segments.length > 0 && currentSrc === null && !loadingSegment) {
      loadSegment(0);
    }
  }, [segments, currentSrc, loadingSegment, loadSegment]);

  // Cleanup blob URL on unmount.
  useEffect(() => {
    return () => { if (currentSrc) URL.revokeObjectURL(currentSrc); };
  }, [currentSrc]);

  // When the audio element gets a new src, auto-play if we were already playing.
  useEffect(() => {
    if (!audioRef.current || !currentSrc) return;
    audioRef.current.load();
    if (playing) {
      audioRef.current.play().catch(() => { /* browser autoplay policy */ });
    }
  }, [currentSrc, playing]);

  function onEnded() {
    // Auto-advance to next segment so the recording plays like one long take.
    if (currentIndex < segments.length - 1) {
      setPlaying(true);
      loadSegment(currentIndex + 1);
    } else {
      setPlaying(false);
    }
  }

  const totalBytes = segments.reduce((n, s) => n + (s.bytes || 0), 0);

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl p-0 overflow-hidden"
        data-testid="playback-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-200">
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-4 w-4 text-zinc-500" />
            <span>Recording preview</span>
            <span className="text-sm font-normal text-zinc-500">— {session.client_name || session.client_email}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {loading ? "Loading segments…" : `${segments.length} segment${segments.length === 1 ? "" : "s"} · ${bytesHuman(totalBytes)}`}
            {session.retention?.days_remaining != null && (
              <> · {session.retention.days_remaining}d retention left</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-0 max-h-[70vh]">
          {/* LEFT — audio player */}
          <div className="p-5 border-b md:border-b-0 md:border-r border-zinc-200 flex flex-col gap-4 min-h-[300px]">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-sm text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            ) : segments.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-sm text-zinc-500">
                <XCircle className="h-8 w-8 text-zinc-400 mb-2" />
                No audio segments on disk — this recording has been purged.
              </div>
            ) : (
              <>
                <div className="text-xs text-zinc-500">
                  Now playing segment <b className="text-zinc-950">{currentIndex + 1}</b> of {segments.length}
                  {loadingSegment && (<span className="ml-2 inline-flex items-center text-zinc-400"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> loading…</span>)}
                </div>
                <audio
                  ref={audioRef}
                  src={currentSrc || undefined}
                  controls
                  className="w-full"
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onEnded={onEnded}
                  data-testid="playback-audio"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentIndex >= segments.length - 1 || loadingSegment}
                    onClick={() => loadSegment(currentIndex + 1)}
                    data-testid="playback-next-segment"
                  >
                    <SkipForward className="h-3.5 w-3.5 mr-1.5" /> Next segment
                  </Button>
                  <span className="text-2xs text-zinc-500 font-mono-num">
                    {bytesHuman(segments[currentIndex]?.bytes || 0)}
                  </span>
                </div>

                {/* Compact segment list for random-access jumps */}
                <div className="mt-2">
                  <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">Jump to</div>
                  <div className="flex flex-wrap gap-1">
                    {segments.map((s, i) => (
                      <button
                        key={s.index}
                        type="button"
                        onClick={() => { setPlaying(true); loadSegment(i); }}
                        className={cn(
                          "text-2xs font-mono-num tabular-nums px-2 py-1 rounded border transition-colors",
                          i === currentIndex
                            ? "border-zinc-950 bg-zinc-950 text-white"
                            : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400",
                        )}
                        data-testid={`playback-jump-${s.index}`}
                      >
                        #{s.index + 1}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-auto pt-3 border-t border-zinc-100">
                  <Button
                    onClick={onRegenerate}
                    className="w-full bg-amber-700 hover:bg-amber-800 text-white h-9"
                    data-testid="playback-regenerate"
                  >
                    <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate note from this recording
                  </Button>
                  <p className="mt-1 text-2xs text-zinc-500 leading-tight">
                    A fresh transcription + LLM pass runs on the stored audio. The current note stays in place until the new one lands.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* RIGHT — existing SOAP note */}
          <div className="p-5 overflow-y-auto max-h-[70vh]" data-testid="playback-note-pane">
            <div className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5 mb-3">
              <FileText className="h-3 w-3" /> Existing SOAP note
            </div>
            {noteLoading ? (
              <div className="text-sm text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading note…
              </div>
            ) : !session.note_id ? (
              <div className="text-sm text-zinc-500 italic">
                No SOAP note has been generated for this recording yet.
              </div>
            ) : !note ? (
              <div className="text-sm text-zinc-500 italic">
                Could not load the linked SOAP note.
              </div>
            ) : (
              <div className="space-y-3">
                <NoteSection label="Subjective" text={note.subjective} />
                <NoteSection label="Objective" text={note.objective} />
                <NoteSection label="Assessment" text={note.assessment} />
                <NoteSection label="Plan" text={note.plan} />
                {note.transcript && (
                  <details className="mt-4">
                    <summary className="text-2xs uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-800">
                      Raw transcript
                    </summary>
                    <pre className="mt-2 text-2xs text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {note.transcript}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NoteSection({ label, text }) {
  if (!text) return null;
  return (
    <div>
      <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{text}</div>
    </div>
  );
}
